import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Snowflake } from "lucide-react";
import { BRAND, IMG } from "@/lib/site";
import { useCountdown, LAUNCH_AT } from "@/lib/launch";

const UNITS = [
  { key: "days", label: "Tage" },
  { key: "hours", label: "Stunden" },
  { key: "minutes", label: "Minuten" },
  { key: "seconds", label: "Sekunden" },
];

const pad = (n) => String(n).padStart(2, "0");

const ComingSoon = () => {
  const t = useCountdown();

  React.useEffect(() => {
    document.title = "Season eröffnet 2026 | Bremer Schneemann – Winterdienst Bremen";
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,follow");
  }, []);
  const launchLabel = LAUNCH_AT.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <main
      data-testid="coming-soon"
      className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-[#062B52] text-white"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.17]"
        style={{
          backgroundImage: `url(${IMG.gritterParking})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_15%_0%,rgba(7,87,184,0.45),transparent_60%)]" />
      <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-[#FF6B00]/10 blur-[90px]" />

      <div className="relative mx-auto flex w-full max-w-[1180px] flex-1 flex-col px-6 py-10 sm:px-10">
        <img
          src={BRAND.logo}
          alt={BRAND.name}
          data-testid="coming-soon-logo"
          className="h-14 w-auto self-start sm:h-16"
        />

        <div className="flex flex-1 flex-col justify-center py-12 sm:py-16">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-[760px]"
          >
            <span className="font-mono-label flex items-center gap-2 text-[#8FB6E4]">
              <Snowflake className="h-3.5 w-3.5 text-[#FF6B00]" />
              / Winterdienst Bremen · Saison 2026/27
            </span>
            <h1 className="mt-5 text-4xl font-black leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Season eröffnet
              <br />
              <span className="text-[#FF6B00]">2026.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#C5D8EE] sm:text-lg">
              Bremer Schneemann startet am {launchLabel} um 06:00 Uhr. Ab dann finden Sie hier
              Schneeräumung, Streudienst und Eisbeseitigung für Privat- und Gewerbeobjekte in
              ganz Bremen.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
            data-testid="coming-soon-countdown"
            className="mt-12 grid max-w-[640px] grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3"
          >
            {UNITS.map((u) => (
              <div
                key={u.key}
                data-testid={`countdown-${u.key}`}
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-5 backdrop-blur-xl"
              >
                <span
                  className="block text-[34px] font-black leading-none tabular-nums tracking-tight sm:text-[42px]"
                  style={{ fontFamily: "Archivo, sans-serif" }}
                >
                  {pad(t[u.key])}
                </span>
                <span className="font-mono-label mt-2.5 block text-[#8FB6E4]">{u.label}</span>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
            className="mt-11"
          >
            <p className="font-mono-label text-[#8FB6E4]">/ Schon jetzt erreichbar</p>
            <div className="mt-3.5 flex flex-col gap-2.5 sm:flex-row">
              <a
                href={BRAND.phoneHref}
                data-testid="coming-soon-phone"
                className="btn btn-orange h-13 px-7 py-3.5 text-[14px] sm:w-auto"
              >
                <Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="coming-soon-whatsapp"
                className="btn btn-wa h-13 px-7 py-3.5 text-[14px] sm:w-auto"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp schreiben
              </a>
            </div>
          </motion.div>
        </div>

        <footer className="relative mt-auto flex flex-col gap-3 border-t border-white/10 pt-6 text-[12.5px] text-[#8FB6E4] sm:flex-row sm:items-center sm:justify-between">
          <span>
            {BRAND.name} · {BRAND.owner} · {BRAND.street}, {BRAND.zip} {BRAND.city}
          </span>
          <span className="flex items-center gap-4">
            <Link to="/impressum" data-testid="coming-soon-impressum" className="hover:text-white">
              Impressum
            </Link>
            <Link to="/datenschutz" data-testid="coming-soon-datenschutz" className="hover:text-white">
              Datenschutz
            </Link>
          </span>
        </footer>
      </div>
    </main>
  );
};

export default ComingSoon;
